import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'
import { X } from 'lucide-react'

import { Combobox } from '@/components/Combobox'
import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Option, Select } from '@/components/ui/select'
import { useAuthStore } from '@/features/auth'
import { useCompanyList } from '@/features/company'
import {
  DELIVERABLE_SET_STATUSES,
  useCreateDeliverableSet,
  useDeliverableSetsStore,
  useUpdateDeliverableSet,
} from '@/features/deliverable-set'
import { isAllowedChangeStatus } from '@/features/deliverable-set/utils/validation.ts'
import { useProjectList } from '@/features/project'
import { useUserList } from '@/features/user'
import { ERoleType } from '@/features/user-management/constants'
import {
  DeliverableSetStatus,
  RequestCreateDeliverableSet,
  RequestUpdateDeliverableSet,
  ResponseDatasetVersion,
  ResponseReportingEvent,
  User,
} from '@/schema'
import { cn, formatFullName } from '@/utils/common'
import {
  DEFAULT_GET_ALL_LIMIT,
  MAX_TEXT_LENGTH,
  SortDirection,
} from '@/utils/constants'

import { getReviewerUsers, getViewerUsers, isAssignedInProject } from '../utils'

import { DatasetVersionInput } from './DatasetVersionInput'

export interface DeliverableSetFormData {
  companyId: string
  projectId: string
  reportingEvent: string
  name: string
  datasetVersion: ResponseDatasetVersion[]
  status: string
  ownerId?: string | null
}

export interface UserWithFullName extends User {
  fullName: string
}

const AddDeliverableSetDialog: React.FC = () => {
  const { t } = useTranslation()

  // Get dialog state from store
  const {
    editingDeliverableSet,
    dialogOpen,
    clearEditingDeliverableSet,
    setDialogOpen,
    contextCompanyId,
    contextProjectId,
  } = useDeliverableSetsStore()

  // Determine if we're in edit mode
  const isEditMode = useMemo(
    () => !!editingDeliverableSet,
    [editingDeliverableSet]
  )
  const currentStatus = useMemo(
    () => editingDeliverableSet?.status || DeliverableSetStatus.DRAFT,
    [editingDeliverableSet]
  )

  const canEditOwnerAndReviewer = useMemo(
    () => [DeliverableSetStatus.DRAFT].includes(currentStatus),
    [currentStatus]
  )

  const shouldDisableStatusChange = useMemo(
    () => [DeliverableSetStatus.DRAFT].includes(currentStatus),
    [currentStatus]
  )

  const [reportingEvents, setReportingEvents] = useState<
    ResponseReportingEvent[]
  >([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const datasetVersionFieldOnChangeRef = useRef<
    ((value: ResponseDatasetVersion[]) => void) | null
  >(null)

  // Initialize React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    register,
    formState: { errors, isValid },
    setValue,
    setError,
    trigger,
    watch,
  } = useForm<DeliverableSetFormData>({
    defaultValues: {
      companyId: '',
      projectId: '',
      reportingEvent: '',
      name: '',
      datasetVersion: [],
      status: DeliverableSetStatus.DRAFT,
      ownerId: null,
    },
  })

  // Watch companyId to enable project list
  const companyIdWatch = watch('companyId')
  const projectIdWatch = watch('projectId')
  const ownerIdWatch = watch('ownerId')
  const datasetVersionWatch = watch('datasetVersion')
  const nameWatch = watch('name')

  // Get user roles and company access
  const roles = useAuthStore((state) => state.roles)
  const getCompaniesFromRoles = useAuthStore(
    (state) => state.getCompaniesFromRoles
  )

  // Check if user has organization admin role
  const isOrgAdmin = useMemo(() => {
    return roles?.some(
      (role) => role.role_name === ERoleType.ORGANIZATION_ADMIN
    )
  }, [roles])

  // Fetch reference data using React Query hooks
  const {
    data: { data: companiesData = [] } = {},
    isLoading: isLoadingCompanies,
  } = useCompanyList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'name', direction: SortDirection.ASC },
    enabled: !!dialogOpen,
  })

  // Filter companies based on user role
  const availableCompanies = useMemo(() => {
    if (isOrgAdmin) {
      // Org admin can see all companies
      return companiesData
    }
    // Non-org admin users can only see companies from their roles
    const companiesFromRoles = getCompaniesFromRoles()
    return companiesFromRoles
  }, [isOrgAdmin, companiesData, getCompaniesFromRoles])

  const {
    data: { data: projectsData = [] } = {},
    isLoading: isLoadingProjects,
  } = useProjectList({
    rowsPerPage: DEFAULT_GET_ALL_LIMIT,
    sort: { column: 'project_name', direction: SortDirection.ASC },
    filters: { companyId: companyIdWatch },
    enabled: !!companyIdWatch && !!dialogOpen,
  })

  const { data: { data: usersData = [] } = {}, isLoading: isLoadingUsers } =
    useUserList({
      rowsPerPage: DEFAULT_GET_ALL_LIMIT,
      enabled: !!dialogOpen,
    })

  // Get selected project to determine project type
  const selectedProject = useMemo(() => {
    if (!projectIdWatch || !projectsData.length) return null
    return projectsData.find((project) => project.id === projectIdWatch) || null
  }, [projectIdWatch, projectsData])

  const users: UserWithFullName[] = useMemo(() => {
    return usersData.map((user) => ({
      ...user,
      fullName: formatFullName(user.first_name, user.last_name),
    }))
  }, [usersData])

  // Create and update mutations
  const createMutation = useCreateDeliverableSet()
  const updateMutation = useUpdateDeliverableSet()

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && editingDeliverableSet) {
      const company = availableCompanies.find(
        (company) => company.id === editingDeliverableSet.company_id
      )
      setValue('companyId', company?.id || '')
      setValue('projectId', editingDeliverableSet.project_id)
      setValue('reportingEvent', editingDeliverableSet.reporting_event_id)
      setValue('name', editingDeliverableSet.deliverable_set_name || '')
      setValue(
        'datasetVersion',
        editingDeliverableSet.dataset_versions as unknown as ResponseDatasetVersion[]
      )
      setValue('status', editingDeliverableSet.status)
      setValue('ownerId', editingDeliverableSet.owner_id || null)
    } else if (!isEditMode && dialogOpen) {
      // Auto-populate company and project when opening dialog from Project Detail page
      if (contextCompanyId) {
        setValue('companyId', contextCompanyId)
      }
      if (contextProjectId) {
        setValue('projectId', contextProjectId)
      }
    }
  }, [
    isEditMode,
    editingDeliverableSet,
    setValue,
    dialogOpen,
    contextCompanyId,
    contextProjectId,
    availableCompanies,
  ])

  // Reset form when dialog closes
  useEffect(() => {
    if (!dialogOpen) {
      reset({
        companyId: '',
        projectId: '',
        reportingEvent: '',
        name: '',
        datasetVersion: [],
        status: DeliverableSetStatus.DRAFT,
        ownerId: null,
      })
      createMutation.reset()
      updateMutation.reset()
      setErrorMsg(null)
      // Clear edit state if in edit mode
      if (isEditMode) {
        clearEditingDeliverableSet()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, reset])

  useEffect(() => {
    if (!projectIdWatch || !projectsData?.length) return

    setReportingEvents(
      projectsData.find((project) => project.id === projectIdWatch)
        ?.reporting_events || []
    )
  }, [projectIdWatch, projectsData])

  // Re-validate ownerId when reviewerIds or ownerId changes
  useEffect(() => {
    trigger('ownerId')
  }, [ownerIdWatch, trigger])

  const assignOwners = React.useMemo(() => {
    if (!dialogOpen) return []

    return users.filter((u) => {
      const isOrgAdmin = u.roles?.some(
        (r) => r.role_name === ERoleType.ORGANIZATION_ADMIN
      )
      const isAssignedForProject = u.roles?.some((r) =>
        isAssignedInProject(
          r,
          projectIdWatch || editingDeliverableSet?.project_id
        )
      )

      return isOrgAdmin || isAssignedForProject
    })
  }, [dialogOpen, editingDeliverableSet?.project_id, projectIdWatch, users])

  const onSubmit = (data: DeliverableSetFormData) => {
    setErrorMsg(null)
    const formData = {
      deliverable_set_name: data.name.trim(),
      project_id: data.projectId,
      reporting_event_id: data.reportingEvent,
      dataset_version_ids:
        data.datasetVersion.length > 0
          ? data.datasetVersion.map((v) => v.id || '')
          : undefined,
      status: data.status || DeliverableSetStatus.DRAFT,
      owner_id: data.ownerId || null,
    }

    if (isEditMode && editingDeliverableSet) {
      // Update deliverable set
      updateMutation.mutate(
        {
          id: editingDeliverableSet.id,
          data: formData as RequestUpdateDeliverableSet,
        },
        {
          onSuccess: () => {
            triggerToast(
              'success',
              t('deliverableSet.dialog.messages.updateSuccess')
            )
            setDialogOpen(false)
            clearEditingDeliverableSet()
          },
          onError: (error: unknown) => {
            // Extract error message from axios error response
            const axiosError = error as AxiosError<{
              error?: { message?: string }
              message?: string
            }>
            const errorMessage =
              axiosError.response?.data?.error?.message ||
              axiosError.response?.data?.message ||
              axiosError.message ||
              t('deliverableSet.dialog.messages.errorGeneric')

            // Check if error is related to duplicate name
            const isDuplicateNameError =
              errorMessage.toLowerCase().includes('already exists') &&
              errorMessage.toLowerCase().includes('deliverable set')

            if (isDuplicateNameError) {
              // Set error on the name field
              setError('name', {
                type: 'manual',
                message: errorMessage,
              })
            } else {
              setErrorMsg(errorMessage)
            }
          },
        }
      )
    } else {
      // Create deliverable set
      createMutation.mutate(formData as RequestCreateDeliverableSet, {
        onSuccess: () => {
          triggerToast(
            'success',
            t('deliverableSet.dialog.messages.addSuccess')
          )
          setDialogOpen(false)
          reset()
        },
        onError: (error: unknown) => {
          // Extract error message from axios error response
          const axiosError = error as AxiosError<{
            error?: { message?: string }
            message?: string
          }>
          const errorMessage =
            axiosError.response?.data?.error?.message ||
            axiosError.response?.data?.message ||
            axiosError.message ||
            t('deliverableSet.dialog.messages.errorGeneric')

          // Check if error is related to duplicate name
          const isDuplicateNameError =
            errorMessage.toLowerCase().includes('already exists') &&
            errorMessage.toLowerCase().includes('deliverable set')

          if (isDuplicateNameError) {
            // Set error on the name field
            setError('name', {
              type: 'manual',
              message: errorMessage,
            })
          } else {
            setErrorMsg(errorMessage)
          }
        },
      })
    }
  }

  const isLoading = useMemo(() => {
    return isLoadingCompanies || isLoadingProjects || isLoadingUsers
  }, [isLoadingCompanies, isLoadingProjects, isLoadingUsers])

  const isSaving = useMemo(
    () => createMutation.isPending || updateMutation.isPending,
    [createMutation.isPending, updateMutation.isPending]
  )

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setDialogOpen(newOpen)
      if (!newOpen && isEditMode) {
        clearEditingDeliverableSet()
      }
    },
    [setDialogOpen, isEditMode, clearEditingDeliverableSet]
  )

  /**
   * Get reviewer and viewer users
   * @returns The reviewer and viewer users
   */
  const reviewerUsers = useMemo(() => {
    if (!editingDeliverableSet) return []

    const { users: reviewers } = getReviewerUsers(editingDeliverableSet)
    const { users: viewers } = getViewerUsers(editingDeliverableSet)

    return [...reviewers, ...viewers]
  }, [editingDeliverableSet])

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="!overflow-hidden flex flex-col max-h-[95dvh]">
          <DialogHeader className="space-y-0.5 flex-shrink-0">
            <DialogTitle className="m-0 font-semibold text-md text-primary-900">
              {isEditMode
                ? t('deliverableSet.dialog.editTitle')
                : t('deliverableSet.dialog.addTitle')}
            </DialogTitle>
            <DialogDescription className="text-sm text-tertiary-600">
              {isEditMode
                ? t('deliverableSet.dialog.editDescription')
                : t('deliverableSet.dialog.addDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 px-6 -mx-6 overflow-x-hidden overflow-y-auto">
            {errorMsg && (
              <div className="w-full px-4 py-3 mb-4 text-sm border border-solid rounded-lg text-error-700 bg-error-50 border-error-200">
                {errorMsg}
              </div>
            )}

            <form
              id="deliverable-set-form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4 pb-1"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label htmlFor="company" className="text-sm font-medium">
                    {t('deliverableSet.dialog.fields.company')}
                  </label>
                  <Controller
                    name="companyId"
                    control={control}
                    rules={{
                      required: t(
                        'deliverableSet.dialog.validation.companyRequired'
                      ),
                    }}
                    render={({ field }) => (
                      <Combobox
                        value={field.value}
                        onChange={(val) => {
                          field.onChange(val)
                          // Reset project and reporting event when company changes
                          setValue('projectId', '')
                          setValue('reportingEvent', '')
                          setReportingEvents([])
                        }}
                        disabled={
                          isLoadingCompanies ||
                          !availableCompanies.length ||
                          isSaving ||
                          !!contextCompanyId
                        }
                        fullWidth
                        placeholder={t(
                          'deliverableSet.dialog.placeholders.selectCompany'
                        )}
                        options={availableCompanies.map((company) => ({
                          value: company.id,
                          label: company.name,
                        }))}
                        clearable={!contextCompanyId}
                      />
                    )}
                  />
                  {errors.companyId && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.companyId.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label htmlFor="project" className="text-sm font-medium">
                    {t('deliverableSet.dialog.fields.project')}
                  </label>
                  <Controller
                    name="projectId"
                    control={control}
                    rules={{
                      required: t(
                        'deliverableSet.dialog.validation.projectRequired'
                      ),
                    }}
                    disabled={!companyIdWatch}
                    render={({ field }) => (
                      <Combobox
                        value={field.value}
                        onChange={(val) => {
                          field.onChange(val)
                          // Reset reporting event when project changes
                          setValue('reportingEvent', '')
                          setReportingEvents([])
                          setValue('datasetVersion', [])
                        }}
                        disabled={
                          isLoadingProjects ||
                          !projectsData.length ||
                          isSaving ||
                          !companyIdWatch ||
                          !!contextProjectId
                        }
                        fullWidth
                        placeholder={t(
                          'deliverableSet.dialog.placeholders.selectProject'
                        )}
                        options={projectsData.map((project) => ({
                          value: project.id,
                          label: project.project_name,
                        }))}
                        clearable={!contextProjectId}
                      />
                    )}
                  />
                  {errors.projectId && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.projectId.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="reportingEvent" className="text-sm font-medium">
                  {t('deliverableSet.dialog.fields.reportingEvent')}
                </label>
                <Controller
                  name="reportingEvent"
                  control={control}
                  rules={{
                    required: t(
                      'deliverableSet.dialog.validation.reportingEventRequired'
                    ),
                  }}
                  render={({ field }) => (
                    <Combobox
                      value={field.value}
                      onChange={field.onChange}
                      fullWidth
                      placeholder={t(
                        'deliverableSet.dialog.placeholders.selectReportingEvent'
                      )}
                      options={reportingEvents.map((event) => ({
                        value: event.id,
                        label: event.reporting_event_name,
                      }))}
                      disabled={
                        !companyIdWatch ||
                        !projectIdWatch ||
                        !reportingEvents.length ||
                        isSaving
                      }
                      clearable
                    />
                  )}
                />
                {errors.reportingEvent && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.reportingEvent.message}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label htmlFor="name" className="text-sm font-medium">
                  {t('deliverableSet.dialog.fields.deliverableSet')}
                </label>
                <Input
                  id="name"
                  {...register('name', {
                    maxLength: {
                      value: MAX_TEXT_LENGTH,
                      message: t(
                        'deliverableSet.dialog.validation.nameMaxLength',
                        { max: MAX_TEXT_LENGTH }
                      ),
                    },
                    validate: (value) => {
                      if (!value.trim()) {
                        return false
                      }
                      return true
                    },
                  })}
                  className="bg-white border-solid rounded-md px-lg py-md gap-md border-primary text-md"
                  placeholder={t(
                    'deliverableSet.dialog.placeholders.enterName'
                  )}
                  maxLength={MAX_TEXT_LENGTH}
                  disabled={isSaving}
                  trailingIcon={
                    <X
                      className={cn(
                        'w-3.5 h-3.5 transition-opacity',
                        nameWatch ? 'opacity-100 cursor-pointer' : 'opacity-0'
                      )}
                    />
                  }
                  onTrailingIconClick={
                    nameWatch
                      ? () => setValue('name', '', { shouldValidate: true })
                      : undefined
                  }
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label htmlFor="datasetVersion" className="text-sm font-medium">
                  {t('deliverableSet.dialog.fields.datasetVersion')}
                </label>
                <Controller
                  name="datasetVersion"
                  control={control}
                  rules={{
                    required: t(
                      'deliverableSet.dialog.validation.datasetVersionRequired'
                    ),
                  }}
                  render={({ field }) => {
                    datasetVersionFieldOnChangeRef.current = field.onChange

                    return (
                      <DatasetVersionInput
                        value={field.value || []}
                        onChange={field.onChange}
                        projectId={selectedProject?.id}
                        projectType={selectedProject?.project_type}
                        disabled={isSaving || !selectedProject}
                        placeholder={t(
                          'deliverableSet.dialog.placeholders.selectDatasetVersion'
                        )}
                      />
                    )
                  }}
                />
                {errors.datasetVersion && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.datasetVersion.message}
                  </p>
                )}
                {datasetVersionWatch && datasetVersionWatch.length > 0 && (
                  <div className="flex flex-wrap gap-2 !mt-3">
                    {datasetVersionWatch.map((v) => {
                      const disabled = isSaving || !projectIdWatch
                      return (
                        <div
                          key={v.id}
                          className={cn(
                            'inline-flex items-center justify-center pl-2 pr-7 py-1 gap-1 rounded-md border border-gray-300 bg-white border-solid text-gray-700',
                            'text-sm font-medium relative max-w-full truncate'
                          )}
                        >
                          <div className="max-w-full truncate">{v.name}</div>
                          <Button
                            variant="text"
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              if (!disabled) {
                                const filteredValue =
                                  datasetVersionWatch.filter(
                                    (vv) => vv.id !== v.id
                                  ) || []

                                if (datasetVersionFieldOnChangeRef.current) {
                                  datasetVersionFieldOnChangeRef.current(
                                    filteredValue
                                  )
                                } else {
                                  setValue('datasetVersion', filteredValue)
                                }
                              }
                            }}
                            disabled={disabled}
                            className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-sm p-0.5 gap-0"
                            aria-label="Clear selection"
                            leadingIcon={<X className="text-gray-400 size-4" />}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <label htmlFor="status" className="text-sm font-medium">
                  {t('deliverableSet.dialog.fields.status')}
                </label>
                <Controller
                  name="status"
                  control={control}
                  rules={{
                    required: t(
                      'deliverableSet.dialog.validation.statusRequired'
                    ),
                  }}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      fullWidth
                      placeholder={t(
                        'deliverableSet.dialog.placeholders.selectStatus'
                      )}
                      disabled={
                        isSaving || !isEditMode || shouldDisableStatusChange
                      }
                      options={
                        Object.keys(DELIVERABLE_SET_STATUSES)
                          .map((key) => {
                            if (!isAllowedChangeStatus(key, currentStatus))
                              return null

                            return {
                              value: key,
                              label:
                                DELIVERABLE_SET_STATUSES[
                                  key as keyof typeof DELIVERABLE_SET_STATUSES
                                ],
                            }
                          })
                          .filter(Boolean) as Option[]
                      }
                    />
                  )}
                />
                {errors.status && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.status.message}
                  </p>
                )}
              </div>

              {/* Owner Field */}
              <div className="space-y-1">
                <label htmlFor="owner" className="text-sm font-medium">
                  {t('deliverableSet.dialog.fields.owner')}
                </label>
                <Controller
                  name="ownerId"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      const inviteUserIds =
                        reviewerUsers?.map((iu) => iu.id) || []

                      if (value && inviteUserIds.includes(value)) {
                        return t(
                          'deliverableSet.dialog.validation.ownerReviewerConflict'
                        )
                      }

                      return true
                    },
                  }}
                  render={({ field }) => (
                    <div className="relative">
                      <Combobox
                        value={field.value || ''}
                        onChange={field.onChange}
                        disabled={
                          isLoadingUsers ||
                          !assignOwners.length ||
                          isSaving ||
                          !canEditOwnerAndReviewer
                        }
                        fullWidth
                        placeholder={t(
                          'deliverableSet.dialog.placeholders.selectOwner'
                        )}
                        clearable
                        options={assignOwners.map((user) => ({
                          value: user.id,
                          label: user.fullName,
                        }))}
                      />
                    </div>
                  )}
                />
                {errors.ownerId && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.ownerId.message}
                  </p>
                )}
              </div>
            </form>
          </div>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3 flex-shrink-0">
            <Button
              type="button"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid bg-white text-[--Colors-Gray-light-700]"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="deliverable-set-form"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-sm border-solid border-transparent text-white bg-brand-solid hover:bg-brand-solid-hover"
              variant="default"
              disabled={isSaving || isLoading || !isValid}
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default AddDeliverableSetDialog
